// Updated useGamePrediction.js - removed the auto-save functionality
import { useState, useCallback, useEffect } from 'react';
import { usePredictionFlow } from './usePredictionFlow';
import { UsageLimiter } from '../utils/usageLimiter';
import { config } from '../config';
import { v4 as uuidv4 } from 'uuid';
import { getPromptsBySport } from '../components/gamePrediction/SportPromptTemplates';
import { getBrazilianStatus } from '../utils/detectBrazil';

export const useGamePrediction = ({ 
  userId, 
  updateUIState,
  tier,
  messages,
  setMessages
}) => {
  const { predictionState, updatePredictionState } = usePredictionFlow();
  const [gameUsageState, setGameUsageState] = useState({ 
    remaining: 0, 
    canUse: false,
    resetTime: null
  });

  useEffect(() => {
    const checkGameUsage = async () => {
      if (userId) {
        const limiter = new UsageLimiter();
        const status = await limiter.checkSlotMachineLimit(userId);
        setGameUsageState(status);
      }
    };
    checkGameUsage();
  }, [userId]);

  // Create a local function that mimics handleRealDemoSubmit
  const sendMessage = async (question, history = []) => {
    const sessionId = uuidv4();
    
    try {
      const requestPayload = {
        question: question,
        history: history.map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        })),
        language: navigator.language || "en",
        isGuest: false,
        metadata: {
          userId: userId,
          sessionId: sessionId,
          conversationId: sessionId
        }
      };

      const response = await fetch(`${config.apiUrl}/ai/ask`, { 
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      let botResponse = "";
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = ""; // Buffer for incomplete lines

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Split by newlines but keep the last potentially incomplete line
        const lines = buffer.split('\n');
        buffer = lines.pop() || ""; // Keep the last line (potentially incomplete)
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'content' && data.text) {
                botResponse += data.text;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }

      // Process any remaining buffered content
      if (buffer && buffer.startsWith('data: ')) {
        try {
          const data = JSON.parse(buffer.slice(6));
          if (data.type === 'content' && data.text) {
            botResponse += data.text;
          }
        } catch (e) {
          console.error('Error parsing final SSE data:', e);
        }
      }

      return botResponse;
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  };

  // Updated runAutomatedGamePrediction function - removed auto-save functionality
  const runAutomatedGamePrediction = useCallback(async (sport = null, options = {}) => {
    // Reset any upgrade prompts
    updateUIState({ showUpgradePrompt: false });
    
    // Check if this is a continuation after paying with Star Coins
    const skipIncrementCheck = predictionState.paidWithStarCoins || options.skipIncrementCheck;
    
    // Always show the modal
    updatePredictionState({
      isRunning: sport ? true : false, // Only running if a sport is selected
      showModal: true,
      step: 0,
      showSportSelector: !sport, // Show sport selector if no sport provided
      selectedSport: sport // Store selected sport if provided
    });
    
    // If no sport selected, return early - the modal will handle selection
    if (!sport) return;

    let conversationHistory = [];
    let selectedGame = null;
    
    try {
      // Get the sport-specific prompts
      const sportPrompts = getPromptsBySport(sport);
      
      // Only check and increment usage if we haven't already paid with Star Coins
      if (!skipIncrementCheck) {
        const limiter = new UsageLimiter();
        const result = await limiter.incrementSlotMachineUsage(userId);
        
        if (!result.success) {
          console.error('Failed to increment usage:', result.error);
          updatePredictionState({
            isRunning: false,
            showModal: true,
            step: 0,
            showSportSelector: !sport,
            selectedSport: sport
          });
          return;
        }
        
        const newStatus = await limiter.checkSlotMachineLimit(userId);
        setGameUsageState(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(newStatus)) {
            return newStatus;
          }
          return prev;
        });
      }
    
      // Clear existing messages
      setMessages([]);
    
      // Step 1: Get today's games and select the best one
      updatePredictionState({ step: 1 });
      
      // Add Portuguese prefix if Brazilian user
      const isBrazilian = getBrazilianStatus();
      const languagePrefix = isBrazilian ? "[Please respond in Brazilian Portuguese] " : "";
      
      const step1Question = languagePrefix + sportPrompts.findBestGame(sport);
      
      setMessages(prev => [...prev, { sender: "user", text: step1Question }]);
      const response1 = await sendMessage(step1Question, conversationHistory);
      setMessages(prev => [...prev, { sender: "bot", text: response1 }]);
      
      conversationHistory.push({ sender: "user", text: step1Question });
      conversationHistory.push({ sender: "bot", text: response1 });
      
      // Extract selected game
      const gamePattern = /(Denver Nuggets|Los Angeles Lakers|Indiana Pacers|New York Knicks|Milwaukee Bucks|Detroit Pistons|Minnesota Timberwolves|Los Angeles Clippers|[A-Z][a-z]+ [A-Z][a-z]+) vs?\. (\w+ ?\w+)/i;
      const gameMatch = response1.match(gamePattern);
      if (gameMatch) {
        selectedGame = `${gameMatch[1]} vs ${gameMatch[2]}`;
        console.log('Detected best value game:', selectedGame);
      } else {
        const valueMentions = response1.match(/(?:Best Early Value|strongest value):([^.]+\.)/i);
        if (valueMentions) {
          const valueGame = valueMentions[1].match(/([\w\s]+) ML/);
          if (valueGame) {
            console.log('Found best value team:', valueGame[1]);
            selectedGame = valueGame[1].trim();
          }
        }
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
    
      // Step 2: Generate comprehensive prediction (combines old steps 2, 3, 4)
      updatePredictionState({ step: 2 });
      
      const internalPrompt = languagePrefix + sportPrompts.generatePrediction(selectedGame, sport);
      const displayPrompt = `Generate a comprehensive prediction for the selected ${sport} game using all available data, news, and statistics.`;
      
      setMessages(prev => [...prev, { sender: "user", text: displayPrompt }]);
      const response2 = await sendMessage(internalPrompt, conversationHistory);
      setMessages(prev => [...prev, { sender: "bot", text: response2 }]);
      
      // Update prediction state with the final results
      updatePredictionState({ 
        step: 3, // Final step
        results: response2,
        paidWithStarCoins: false
      });
      
    } catch (error) {
      console.error("Error in game prediction:", error);
      updatePredictionState({ 
        showModal: true, 
        isRunning: false,
        step: 0,
        showSportSelector: !sport,
        selectedSport: sport
      });
    } finally {
      updatePredictionState({ isRunning: false });
    }
  }, [gameUsageState, userId, tier, predictionState.paidWithStarCoins, updatePredictionState, setMessages, updateUIState]);
  
  return {
    predictionState,
    updatePredictionState,
    gameUsageState,
    runAutomatedGamePrediction
  };
};